/**
 * FileMagic — sniff true container by reading the first 16 bytes (magic bytes).
 *
 * Why this exists:
 *   /repair-audio user manuel.felsing@gmx.de 2026-06-08 uploaded "Warum.mp3"
 *   which was actually a Microsoft ASF / Windows Media Audio file. Browser
 *   decodeAudioData fails (Tier 1 decode_tier_failed in funnel), FFmpeg WASM
 *   5.1.4's wmav2 decoder partly works but emits 60+ "frame_len overflow" /
 *   "Error submitting packet to decoder" errors. Result: choppy / stuttering
 *   output ("abgehackt"). User reported as a tool bug.
 *
 *   By detecting the magic bytes upfront we can WARN the user that the file
 *   isn't actually what its extension says and recommend a desktop converter
 *   before processing wastes their time.
 *
 * Public API:
 *   await window.FileMagic.sniff(file) →
 *     { container, codec, ext, extMatch, displayName, recommendation }
 */
(function(){
'use strict';

if (window.FileMagic && !window.FileMagic.__isStub) return;

// ASF GUID: 30 26 B2 75 8E 66 CF 11 A6 D9 00 AA 00 62 CE 6C
// First 8 bytes are enough to discriminate.
var ASF_GUID = [0x30, 0x26, 0xB2, 0x75, 0x8E, 0x66, 0xCF, 0x11];

function matchPrefix(bytes, prefix, offset) {
    offset = offset || 0;
    if (bytes.length < offset + prefix.length) return false;
    for (var i = 0; i < prefix.length; i++) {
        if (bytes[offset + i] !== prefix[i]) return false;
    }
    return true;
}

function ascii4(bytes, offset) {
    return String.fromCharCode(bytes[offset], bytes[offset+1], bytes[offset+2], bytes[offset+3]);
}

function sniffBytes(b) {
    if (!b || b.length < 12) return { container: 'unknown', codec: null };

    // RIFF/WAVE
    if (ascii4(b, 0) === 'RIFF' && ascii4(b, 8) === 'WAVE') {
        return { container: 'wav', codec: 'pcm', displayName: 'WAV' };
    }
    // fLaC
    if (ascii4(b, 0) === 'fLaC') {
        return { container: 'flac', codec: 'flac', displayName: 'FLAC' };
    }
    // OggS
    if (ascii4(b, 0) === 'OggS') {
        return { container: 'ogg', codec: null, displayName: 'Ogg Vorbis/Opus' };
    }
    // ID3v2 → MP3 (definitive)
    if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) {
        return { container: 'mp3', codec: 'mp3', displayName: 'MP3' };
    }
    // ASF / WMA (NEW — Manuel's case)
    if (matchPrefix(b, ASF_GUID)) {
        return {
            container: 'asf',
            codec: 'wmav2',  // best-effort guess — most ASF audio files are WMA v2
            displayName: 'Windows Media Audio (WMA)',
            recommendation: 'wma_known_issue'
        };
    }
    // MPEG audio frame sync — MP3 / MP2 / MP1
    if (b[0] === 0xFF && (b[1] & 0xF0) === 0xF0) {
        var layerBits = (b[1] >> 1) & 0x03;  // 00=AAC ADTS, 01=MP3, 10=MP2, 11=MP1
        if (layerBits === 0x00) return { container: 'aac', codec: 'aac', displayName: 'AAC' };
        if (layerBits === 0x01) return { container: 'mp3', codec: 'mp3', displayName: 'MP3' };
        if (layerBits === 0x02) return { container: 'mp3', codec: 'mp2', displayName: 'MP2' };
        if (layerBits === 0x03) return { container: 'mp3', codec: 'mp1', displayName: 'MP1' };
    }
    if (b[0] === 0xFF && (b[1] & 0xE0) === 0xE0) {
        return { container: 'mp3', codec: 'mp3', displayName: 'MP3' };
    }
    // ISO Base Media — ftyp at offset 4
    if (b.length >= 12 && ascii4(b, 4) === 'ftyp') {
        var brand = ascii4(b, 8);
        var isVideo = (brand === 'mp42' || brand === 'isom' || brand === 'qt  ' || brand === 'mp41');
        return {
            container: isVideo ? 'mp4' : 'm4a',
            codec: 'aac',
            displayName: isVideo ? 'MP4 (video container)' : 'M4A / AAC'
        };
    }
    // EBML — WebM / Matroska
    if (b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3) {
        return { container: 'webm', codec: null, displayName: 'WebM / Matroska' };
    }
    // AIFF — FORM ... AIFF
    if (ascii4(b, 0) === 'FORM' && ascii4(b, 8) === 'AIFF') {
        return { container: 'aiff', codec: 'pcm', displayName: 'AIFF' };
    }
    return { container: 'unknown', codec: null, displayName: 'Unknown' };
}

// Extension-to-container expectation. False if mismatched (e.g. user's
// "Warum.mp3" with container='asf' → matches mp3 expectations? NO — different
// container expected).
function matchesExt(container, ext) {
    if (!ext || container === 'unknown') return null;
    ext = ext.toLowerCase();
    var map = {
        wav: ['wav', 'wave'],
        flac: ['flac', 'flc'],
        ogg: ['ogg', 'oga', 'opus'],
        mp3: ['mp3', 'mp2', 'mp1'],
        aac: ['aac', 'adts'],
        m4a: ['m4a', 'm4b', 'm4r', 'm4p'],
        mp4: ['mp4', 'mov', 'm4v', 'qt'],
        webm: ['webm', 'mkv', 'mka'],
        aiff: ['aiff', 'aif', 'aifc'],
        // ASF/WMA — when the file extension is .wma, this matches.
        // When the extension is .mp3 and the magic says asf, this DOES NOT.
        asf: ['wma', 'wmv', 'asf']
    };
    var allowed = map[container] || [];
    return allowed.indexOf(ext) !== -1;
}

async function sniff(file) {
    if (!file || !file.size) {
        return { container: 'unknown', codec: null, ext: null, extMatch: null, displayName: 'Unknown' };
    }
    var ext = (file.name || '').split('.').pop().toLowerCase();
    var headBuf = await file.slice(0, 16).arrayBuffer();
    var bytes = new Uint8Array(headBuf);
    var info = sniffBytes(bytes);
    info.ext = ext;
    info.extMatch = matchesExt(info.container, ext);
    return info;
}

/* ═══ identify(file) — what the BYTES are, for a tool that cuts or copies ═══
 *
 * sniff() above answers «does the name lie?» from 16 bytes. A tool that keeps
 * the source's own bytes (a cut without re-encoding, «format: same as source»)
 * needs more: the codec, and the extension that honestly names those bytes.
 * Until 2026-10-30 /trim took both from the NAME, and every lie in a name
 * became a broken result (RU, October, 15 623 sessions with a file):
 *   · `.wma` was mapped to «mp3» (FormatSmart, output-side mapping) — the menu
 *     said «MP3 (source)» and a cut ran `-c copy` of WMA into an MP3 muxer:
 *     «Exactly one MP3 audio stream is required», both attempts, every time;
 *   · an AAC-in-MP4 or a raw ADTS stream saved as `.mp3` (music downloaders do
 *     this) — the same muxer error, or a re-encode under a name that lied;
 *   · an MP3 padded with zeros to a round size (1 MiB, 512 KiB — the most
 *     common sizes on the tool) was declined by the frame cutter.
 * One reader, one answer: the container from the signature, the codec from
 * the stream itself (MPEG frame chain, ADTS frame chain, WAVE fmt code, the
 * first Ogg packet, MediaAudioProbe for MP4/Matroska), and an ID3v2 tag is
 * looked BEHIND — it fronts AAC and FLAC as readily as MP3.
 *
 * → { kind, codec, ext, label, nameExt, extLies, details }
 *   kind  'mpeg' | 'adts' | 'mp4' | 'wav' | 'aiff' | 'flac' | 'ogg' | 'webm'
 *         | 'matroska' | 'asf' | 'caf' | 'amr' | 'playlist' | 'text' | 'image'
 *         | 'document' | 'empty' | 'unknown'
 *   codec what the audio inside is ('mp3', 'aac', 'pcm', 'adpcm_ima', 'opus' …),
 *         '' when the container was read but its codec was not
 *   ext   the extension these bytes deserve ('mp3', 'aac', 'm4a', 'wav' …) —
 *         the one a file made of them must carry
 *   label for a menu: «MP3», «AAC», «WMA» …
 */
var IDENTIFY_WINDOW = 65536;

function readRange(file, from, len) {
    return file.slice(from, Math.min(file.size, from + len)).arrayBuffer().then(function (b) { return new Uint8Array(b); });
}
function nameExtOf(file) {
    var n = String((file && file.name) || '');
    var dot = n.lastIndexOf('.');
    return dot > 0 ? n.slice(dot + 1).toLowerCase() : '';
}
function u32le(b, o) { return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0; }
function u16le(b, o) { return b[o] | (b[o + 1] << 8); }
function u32be(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }

/* The tag's full length, footer included; 0 = no ID3v2 here. */
function id3v2Length(b, o) {
    o = o || 0;
    if (b.length < o + 10 || b[o] !== 0x49 || b[o + 1] !== 0x44 || b[o + 2] !== 0x33) return 0;
    var s = ((b[o + 6] & 0x7F) << 21) | ((b[o + 7] & 0x7F) << 14) | ((b[o + 8] & 0x7F) << 7) | (b[o + 9] & 0x7F);
    return 10 + s + ((b[o + 5] & 0x10) ? 10 : 0);
}

/* ADTS: 12-bit sync, layer 00. MPEG audio shares the first 11 bits — the
   layer field is what tells them apart, and a chain of frames is what says
   it is a stream and not two lucky bytes. */
var ADTS_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];
function adtsAt(b, o) {
    if (o + 7 > b.length || b[o] !== 0xFF || (b[o + 1] & 0xF6) !== 0xF0) return null;
    var sf = (b[o + 2] >> 2) & 0x0F;
    if (sf > 12) return null;
    var len = ((b[o + 3] & 0x03) << 11) | (b[o + 4] << 3) | ((b[o + 5] >> 5) & 0x07);
    var hdr = (b[o + 1] & 0x01) ? 7 : 9;
    if (len <= hdr) return null;
    return { len: len, sf: sf, rate: ADTS_RATES[sf], ch: ((b[o + 2] & 0x01) << 2) | ((b[o + 3] >> 6) & 0x03), profile: (b[o + 2] >> 6) & 0x03 };
}
function adtsChain(b, o, need) {
    var first = adtsAt(b, o);
    if (!first) return 0;
    var n = 1, at = o + first.len;
    while (n < need) {
        if (at + 7 > b.length) return at >= b.length ? n : 0;   // the window ended on a frame boundary
        var h = adtsAt(b, at);
        if (!h || h.sf !== first.sf) return 0;
        n++; at += h.len;
    }
    return n;
}
function findAdts(b, from, need) {
    for (var p = from; p + 7 <= b.length; p++) {
        if (b[p] !== 0xFF) continue;
        if (adtsChain(b, p, need) >= need) return p;
    }
    return -1;
}

/* Loaded only when the bytes call for it — a page that never meets an MP3 or
   an MP4 never pays for the reader. __jsSrc gives the versioned URL. */
var loading = {};
function need(global, path) {
    if (window[global]) return Promise.resolve(window[global]);
    if (loading[global]) return loading[global];
    if (typeof document === 'undefined') return Promise.resolve(null);
    var url = (typeof window.__jsSrc === 'function') ? window.__jsSrc(path) : path;
    loading[global] = new Promise(function (resolve) {
        var s = document.createElement('script');
        var n = document.querySelector('script[nonce]');
        if (n && n.nonce) s.setAttribute('nonce', n.nonce);
        s.src = url;
        s.onload = function () { resolve(window[global] || null); };
        s.onerror = function () { loading[global] = null; resolve(null); };
        document.head.appendChild(s);
    });
    return loading[global];
}

var LABEL = { mp3: 'MP3', mp2: 'MP2', aac: 'AAC', m4a: 'M4A', wav: 'WAV', aiff: 'AIFF', flac: 'FLAC', ogg: 'OGG', opus: 'OPUS',
              webm: 'WEBM', mka: 'MKA', wma: 'WMA', caf: 'CAF', amr: 'AMR', mp4: 'MP4' };
var WAV_CODEC = { 1: 'pcm', 3: 'float', 2: 'adpcm_ms', 6: 'alaw', 7: 'mulaw', 0x11: 'adpcm_ima', 0x31: 'gsm', 0x55: 'mp3', 0x50: 'mp2', 0x1600: 'aac', 0x0161: 'wma' };
var MP4_EXT = { aac: 'm4a', alac: 'm4a', mp3: 'm4a', 'ac-3': 'm4a', 'e-ac-3': 'm4a', opus: 'm4a', flac: 'm4a' };
var FAMILY = {  // which name extensions honestly carry each kind
    mpeg: ['mp3', 'mp2', 'mp1', 'mpga'], adts: ['aac', 'adts'], mp4: ['m4a', 'mp4', 'm4b', 'm4r', 'm4p', 'mov', 'm4v', '3gp', '3g2', '3gpp'],
    wav: ['wav', 'wave'], aiff: ['aiff', 'aif', 'aifc'], flac: ['flac'], ogg: ['ogg', 'oga', 'opus', 'spx'],
    webm: ['webm', 'weba'], matroska: ['mkv', 'mka', 'webm'], asf: ['wma', 'asf', 'wmv'], caf: ['caf'], amr: ['amr', '3ga']
};

function result(kind, codec, ext, file, details) {
    var nx = nameExtOf(file);
    var fam = FAMILY[kind];
    return {
        kind: kind, codec: codec || '', ext: ext || '', label: LABEL[ext] || (ext ? ext.toUpperCase() : ''),
        nameExt: nx, extLies: !!(fam && nx && fam.indexOf(nx) === -1), details: details || {}
    };
}

async function identifyWav(file, head, riff) {
    // Chunks are walked with small reads: a 3-hour WAV is never read whole.
    var pos = 12, guard = 0, fmt = null, dataAt = -1, dataLen = 0, fact = -1;
    while (pos + 8 <= file.size && guard++ < 256) {
        var h = pos + 8 <= head.length ? head.subarray(pos, pos + 8) : await readRange(file, pos, 8);
        if (h.length < 8) break;
        var id = String.fromCharCode(h[0], h[1], h[2], h[3]);
        var size = u32le(h, 4);
        if (id === 'fmt ') {
            var f = pos + 8 + Math.max(size, 16) <= head.length ? head.subarray(pos + 8, pos + 8 + Math.max(size, 16)) : await readRange(file, pos + 8, Math.max(size, 40));
            var code = u16le(f, 0);
            if (code === 0xFFFE && f.length >= 26) code = u16le(f, 24);   // EXTENSIBLE: the subformat GUID starts with the code
            fmt = { code: code, channels: u16le(f, 2), sampleRate: u32le(f, 4), blockAlign: u16le(f, 12), bits: u16le(f, 14),
                    samplesPerBlock: (f.length >= 20 && size >= 20) ? u16le(f, 18) : 0, fmtAt: pos, fmtSize: size };
        } else if (id === 'fact') {
            fact = pos;
        } else if (id === 'data') {
            dataAt = pos + 8;
            // A streaming writer leaves 0 or 0xFFFFFFFF: the data runs to the end of the file.
            dataLen = (size === 0 || size === 0xFFFFFFFF || dataAt + size > file.size) ? file.size - dataAt : size;
            break;
        }
        pos += 8 + size + (size & 1);
    }
    var codec = fmt ? (WAV_CODEC[fmt.code] || ('wav_' + fmt.code)) : '';
    return result('wav', codec, 'wav', file, { fmt: fmt, dataAt: dataAt, dataLen: dataLen, factAt: fact, rf64: riff === 'RF64' });
}

async function identifyMpeg(file, at, win, slug) {
    // The verdict «is this an MPEG stream» has one owner: Mp3FrameTrim.sniffMpeg
    // (ID3 length, a 64 KB window behind it, a chain of frames of ONE stream —
    // MpegStreamVerdictHasOneOwnerTest). It also reports the files whose first
    // bytes nominate MP3 and whose stream does not (mp3_sniff_declined).
    var T = await need('Mp3FrameTrim', '/js/mp3-frame-trim.js');
    if (!T || typeof T.sniffMpeg !== 'function') return null;
    if (!(await T.sniffMpeg(file, { slug: slug }))) return null;
    // The layer of the first frame the stream verdict found names the codec.
    var layer = 'Layer3';
    if (typeof T.locateStream === 'function') {
        try { var loc = T.locateStream(win, 0, win.length); if (loc && loc.first && loc.first.hdr) layer = loc.first.hdr.layer; } catch (_) {}
    }
    var codec = layer === 'Layer2' ? 'mp2' : (layer === 'Layer1' ? 'mp1' : 'mp3');
    return result('mpeg', codec, codec === 'mp3' ? 'mp3' : 'mp2', file, { at: at, id3: at > 0 });
}

async function identifyContainerAt(file, b, at) {
    var s4 = function (o) { return o + 4 <= b.length ? String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]) : ''; };
    if (s4(0) === 'RIFF' && s4(8) === 'WAVE') return identifyWav(file, b, 'RIFF');
    if (s4(0) === 'RF64' && s4(8) === 'WAVE') return identifyWav(file, b, 'RF64');
    if (s4(0) === 'FORM' && (s4(8) === 'AIFF' || s4(8) === 'AIFC')) return result('aiff', s4(8) === 'AIFC' ? 'aifc' : 'pcm', 'aiff', file);
    if (s4(0) === 'fLaC') return result('flac', 'flac', 'flac', file, { at: at });
    if (s4(0) === 'OggS' && b.length > 27) {
        var p = 27 + b[26];
        var codec = '', ext = 'ogg';
        if (s4(p) === 'Opus' && s4(p + 4) === 'Head') { codec = 'opus'; ext = 'opus'; }
        else if (b[p] === 0x01 && s4(p + 1) === 'vorb') codec = 'vorbis';
        else if (b[p] === 0x7F && s4(p + 1) === 'FLAC') codec = 'flac';
        else if (s4(p) === 'Spee') codec = 'speex';
        return result('ogg', codec, ext, file);
    }
    if (b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3) {
        var doc = '';
        for (var i = 4; i + 3 < Math.min(b.length, 64); i++) {
            if (b[i] === 0x42 && b[i + 1] === 0x82) { var n = b[i + 2] & 0x7F; doc = String.fromCharCode.apply(null, Array.prototype.slice.call(b, i + 3, i + 3 + n)); break; }
        }
        var P = await need('MediaAudioProbe', '/js/media-audio-probe.js');
        var pr = P ? await P.probe(file) : null;
        var c = pr && pr.codecs && pr.codecs[0] ? String(pr.codecs[0]).toLowerCase() : '';
        var webm = doc === 'webm';
        return result(webm ? 'webm' : 'matroska', c, webm ? 'webm' : 'mka', file, { doc: doc, hasAudio: pr ? pr.hasAudio : null });
    }
    var box = s4(4);
    if (box === 'ftyp' || box === 'moov' || box === 'mdat' || box === 'wide' || box === 'free' || box === 'skip') {
        var MP = await need('MediaAudioProbe', '/js/media-audio-probe.js');
        var mp = MP ? await MP.probe(file) : null;
        var mc = mp && mp.codecs && mp.codecs[0] ? String(mp.codecs[0]).toLowerCase() : '';
        var brand = box === 'ftyp' ? s4(8) : '';
        return result('mp4', mc, MP4_EXT[mc] || 'm4a', file, { brand: brand, hasAudio: mp ? mp.hasAudio : null, channels: mp ? mp.channels : 0, sampleRate: mp ? mp.sampleRate : 0, durationSec: mp ? mp.durationSec : 0 });
    }
    if (matchPrefix(b, ASF_GUID)) return result('asf', 'wma', 'wma', file);
    if (s4(0) === 'caff') return result('caf', '', 'caf', file);
    if (s4(0) === '#!AM' && b[4] === 0x52) return result('amr', b[5] === 0x2D ? 'amr_wb' : 'amr_nb', 'amr', file);
    return null;
}

function looksLikeText(b) {
    var n = Math.min(b.length, 512), printable = 0;
    if (!n) return false;
    for (var i = 0; i < n; i++) { var c = b[i]; if (c === 9 || c === 10 || c === 13 || (c >= 32 && c < 127) || c >= 0xC0) printable++; }
    return printable / n > 0.95;
}

async function identify(file, opts) {
    if (!file || !file.size) return result('empty', '', '', file);
    var b = await readRange(file, 0, IDENTIFY_WINDOW);
    // ID3v2 fronts MP3, but just as readily AAC and FLAC: look behind it (and
    // behind a second one — some taggers stack them).
    var at = 0, tag = id3v2Length(b, 0), hops = 0;
    while (tag && hops++ < 2) {
        at += tag;
        var nb = await readRange(file, at, IDENTIFY_WINDOW);
        b = nb;
        tag = id3v2Length(b, 0);
    }
    if (!b.length) return result('unknown', '', '', file, { id3Only: at > 0 });
    var c = await identifyContainerAt(file, b, at);
    if (c) { if (at) c.details.id3 = true; return c; }
    // A stream of frames: ADTS (layer 00) or MPEG audio. Both may sit behind a
    // few junk bytes; the chain decides, never the first two bytes.
    if (b[0] === 0xFF && (b[1] & 0xF6) === 0xF0 && adtsChain(b, 0, 3) >= 3) {
        var a = adtsAt(b, 0);
        return result('adts', 'aac', 'aac', file, { at: at, rate: a.rate, channels: a.ch, profile: a.profile, id3: at > 0 });
    }
    var m = await identifyMpeg(file, at, b, opts && opts.slug);
    if (m) return m;
    var ap = findAdts(b, 0, 4);
    if (ap >= 0) { var a2 = adtsAt(b, ap); return result('adts', 'aac', 'aac', file, { at: at + ap, rate: a2.rate, channels: a2.ch, profile: a2.profile, id3: at > 0 }); }
    var s = String.fromCharCode.apply(null, Array.prototype.slice.call(b, 0, 8));
    if (/^#EXTM3U/.test(s)) return result('playlist', '', 'm3u8', file);
    if (b[0] === 0x89 && s.slice(1, 4) === 'PNG' || (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) || s.slice(0, 4) === 'GIF8') return result('image', '', '', file);
    if (s.slice(0, 4) === '%PDF' || (b[0] === 0x50 && b[1] === 0x4B && b[2] === 0x03 && b[3] === 0x04)) return result('document', '', '', file);
    if (looksLikeText(b)) return result('text', '', '', file);
    return result('unknown', '', '', file, { id3: at > 0 });
}

window.FileMagic = {
    __isStub: false,
    version: '1.1.0',
    sniff: sniff,
    identify: identify,
    // Exposed for tests
    _sniffBytes: sniffBytes,
    _matchesExt: matchesExt,
    _adtsAt: adtsAt,
    _id3v2Length: id3v2Length,
    ASF_GUID: ASF_GUID
};

})();
